/**
 * Admin Inventory Management Page
 * View and manage inventory across all PUDO points
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Link2, 
  ArrowLeft, 
  Package,
  Search,
  RefreshCw,
  MapPin,
  Clock,
  Truck,
  CheckCircle,
  Box,
  BarChart3,
  Filter
} from 'lucide-react';

const STATUS_COLORS = {
  at_pudo: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  awaiting_pickup: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  in_storage: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  in_transit: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  pending: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  delivered: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
};

const AdminInventoryManagement = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [inventory, setInventory] = useState({ packages: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (!isAdmin()) {
      toast.error('Access denied');
      navigate('/dashboard');
      return;
    }
    fetchInventory();
  }, [isAdmin, navigate]);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const [inventoryRes, statsRes] = await Promise.all([
        api.get('/admin/inventory/'),
        api.get('/admin/inventory/stats')
      ]);
      setInventory({
        packages: inventoryRes.data?.packages || [],
        stats: statsRes.data || {}
      });
    } catch (error) {
      console.error('Error fetching inventory:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  // Filter packages
  const filteredPackages = inventory.packages.filter(pkg => {
    const matchesSearch = 
      pkg.tracking_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pkg.pudo_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pkg.id?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || pkg.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const stats = inventory.stats?.packages || {};

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <Link2 className="h-7 w-7 text-amber-500" />
              <span className="text-xl font-bold text-white tracking-tight">Liantiao</span>
              <span className="text-xs font-mono text-cyan-400 bg-cyan-500/10 px-2 py-1 rounded border border-cyan-500/20">
                INVENTORY
              </span>
            </div>
          </div>
          <Button
            onClick={fetchInventory}
            variant="outline"
            size="sm"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Box className="h-5 w-5 text-slate-400" />
              <span className="text-slate-400 text-sm">Total</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.total || 0}</p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <MapPin className="h-5 w-5 text-purple-400" />
              <span className="text-slate-400 text-sm">At PUDO</span>
            </div>
            <p className="text-2xl font-bold text-purple-400">{stats.at_pudo || 0}</p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Truck className="h-5 w-5 text-cyan-400" />
              <span className="text-slate-400 text-sm">In Transit</span>
            </div>
            <p className="text-2xl font-bold text-cyan-400">{stats.in_transit || 0}</p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="h-5 w-5 text-yellow-400" />
              <span className="text-slate-400 text-sm">Pending</span>
            </div>
            <p className="text-2xl font-bold text-yellow-400">{stats.pending || 0}</p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="h-5 w-5 text-teal-400" />
              <span className="text-slate-400 text-sm">Delivered</span>
            </div>
            <p className="text-2xl font-bold text-teal-400">{stats.delivered || 0}</p>
          </div>
        </div>

        {/* Products Stats */}
        {inventory.stats?.products && (
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-amber-400" />
              Product Inventory
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-slate-400 text-sm">Total Products</p>
                <p className="text-2xl font-bold text-white">{inventory.stats.products.total || 0}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-slate-400 text-sm">Active Products</p>
                <p className="text-2xl font-bold text-teal-400">{inventory.stats.products.active || 0}</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by tracking number or PUDO location..."
              className="bg-slate-900 border-slate-700 pl-10 text-white"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Filter className="h-4 w-4" />
              Status:
            </div>
            {['all', 'at_pudo', 'awaiting_pickup', 'in_transit', 'pending'].map((status) => (
              <Button
                key={status}
                onClick={() => setStatusFilter(status)}
                variant={statusFilter === status ? 'default' : 'outline'}
                size="sm"
                className={statusFilter === status 
                  ? 'bg-amber-500 text-slate-950' 
                  : 'border-slate-700 text-slate-300'
                }
                data-testid={`filter-${status}`}
              >
                {status === 'all' ? 'All' : status.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              </Button>
            ))}
          </div>
        </div>

        {/* Packages Table */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-slate-800">
            <h3 className="text-sm font-medium text-white uppercase tracking-wide flex items-center gap-2">
              <Package className="h-4 w-4 text-amber-400" />
              Packages in System ({filteredPackages.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="inventory-table">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-900/50">
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">Package</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">Location</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">Status</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">Created</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center">
                      <RefreshCw className="h-6 w-6 text-amber-500 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : filteredPackages.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-slate-400">
                      {inventory.packages.length === 0 ? 'No packages in inventory' : 'No packages match filters'}
                    </td>
                  </tr>
                ) : (
                  filteredPackages.map((pkg) => (
                    <tr 
                      key={pkg.id}
                      className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors"
                      data-testid={`package-row-${pkg.id}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                            <Package className="w-5 h-5 text-cyan-400" />
                          </div>
                          <div>
                            <p className="text-white font-mono text-sm">{pkg.tracking_number || pkg.id?.slice(0, 8)}</p>
                            <p className="text-xs text-slate-500">{pkg.description || 'Package'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-slate-400" />
                          <span className="text-slate-300 text-sm">{pkg.pudo_name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-mono border ${STATUS_COLORS[pkg.status] || STATUS_COLORS.pending}`}>
                          {pkg.status?.replace('_', ' ') || 'pending'}
                        </span>
                      </td>
                      <td className="p-4 text-slate-400 text-sm">
                        {pkg.created_at ? new Date(pkg.created_at).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminInventoryManagement;
